import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { UserProvider } from './contexts/UserContext';
import SiteNav from './components/layout/SiteNav';
import SiteFooter from './components/layout/SiteFooter';
import HomePage from './pages/HomePage';
import DataPage from './pages/DataPage';
import MentorProfilePage from './pages/MentorProfilePage';
import IntroCallBooking from './pages/IntroCallBooking';
import FamilySignupPage from './pages/FamilySignupPage';
import FamilyDashboard from './pages/FamilyDashboard';
import OpsPage from './pages/OpsPage';
import LoginPage from './components/LoginPage';
import Profile from './components/Profile';
import AdminUsers from './components/AdminUsers';
import AdminFeedback from './components/AdminFeedback';
import AdminTemplates from './components/AdminTemplates';
import AdminActivities from './components/AdminActivities';
import About from './components/About';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import MentorMarketplace from './components/MentorMarketplace';
import MentorBooking from './components/MentorBooking';
import MentorBookingSuccess from './components/MentorBookingSuccess';
import BecomeMentor from './components/BecomeMentor';
import MentorDashboard from './components/MentorDashboard';
import MentorProfileEdit from './components/MentorProfileEdit';
import RoleSelectionPage from './components/RoleSelectionPage';
import MessagesPage from './components/MessagesPage';
import ConfirmSession from './components/ConfirmSession';
import VideoRoom from './components/VideoRoom';
import SessionReview from './components/SessionReview';
import AMCDashboard from './components/AMCDashboard';
import AMCAnalytics from './components/AMCAnalytics';
import AMCMissedQuestions from './components/AMCMissedQuestions';
import AMCMissedPractice from './components/AMCMissedPractice';
import AMCSamplePage from './pages/AMCSamplePage';
import WebinarLibraryPage from './pages/WebinarLibraryPage';
import QADigestPage from './pages/QADigestPage';
import WeeklyTipsPage from './pages/WeeklyTipsPage';
import DeadlineCalendarPage from './pages/DeadlineCalendarPage';
import ActivityMapPage from './pages/ActivityMapPage';
import AdmitProfilesPage from './pages/AdmitProfilesPage';
import AdmitProfileDetailPage from './pages/AdmitProfileDetailPage';
import './App.css';

// Routes where the footer is omitted (immersive / task-focused screens)
const NO_FOOTER_PREFIXES = ['/messages', '/video-room', '/confirm-session', '/session-review'];

function AppShell() {
  const location = useLocation();
  const hideFooter = NO_FOOTER_PREFIXES.some((p) => location.pathname.startsWith(p));

  return (
    <div className="App">
      <SiteNav />
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/data" element={<DataPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/family-signup" element={<FamilySignupPage />} />
          <Route path="/dashboard" element={<Navigate to="/mentors" replace />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/feedback" element={<AdminFeedback />} />
          <Route path="/admin/templates" element={<AdminTemplates />} />
          <Route path="/admin/activities" element={<AdminActivities />} />
          <Route path="/admin/ops" element={<OpsPage />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/mentors" element={<MentorMarketplace />} />
          <Route path="/mentors/:id" element={<MentorProfilePage />} />
          <Route path="/intro-call/:id" element={<IntroCallBooking />} />
          <Route path="/mentor-booking" element={<MentorBooking />} />
          <Route path="/mentor-booking-success" element={<MentorBookingSuccess />} />
          <Route path="/family-dashboard" element={<FamilyDashboard />} />
          <Route path="/mentor-sessions" element={<Navigate to="/family-dashboard" replace />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/become-a-mentor" element={<BecomeMentor />} />
          {/* Role Selection */}
          <Route path="/select-role" element={<RoleSelectionPage />} />

          {/* Mentor Portal Routes */}
          <Route path="/mentor/login" element={<Navigate to="/login" replace />} />
          <Route path="/mentor/apply" element={<Navigate to="/become-a-mentor" replace />} />
          <Route path="/mentor/apply/parent" element={<Navigate to="/become-a-mentor" replace />} />
          <Route path="/mentor/dashboard" element={<MentorDashboard />} />
          <Route path="/mentor/profile" element={<MentorProfileEdit />} />

          {/* Session Confirmation (Magic Link) */}
          <Route path="/confirm-session/:token" element={<ConfirmSession />} />

          {/* Video Conferencing */}
          <Route path="/video-room/:sessionType/:sessionId" element={<VideoRoom />} />
          <Route path="/session-review/:sessionType/:sessionId" element={<SessionReview />} />

          {/* AMC Practice */}
          <Route path="/amc" element={<AMCDashboard />} />
          <Route path="/amc-dashboard" element={<Navigate to="/amc" replace />} />
          <Route path="/amc-practice" element={<Navigate to="/amc" replace />} />
          <Route path="/amc-analytics" element={<AMCAnalytics onBack={() => window.history.back()} />} />
          <Route path="/amc-missed-questions" element={<AMCMissedQuestions />} />
          <Route path="/amc-missed" element={<Navigate to="/amc-missed-questions" replace />} />
          <Route path="/amc-missed-practice" element={<AMCMissedPractice />} />
          <Route path="/amc/sample" element={<AMCSamplePage />} />

          {/* Free resources */}
          <Route path="/resources/webinars" element={<WebinarLibraryPage />} />
          <Route path="/resources/qa" element={<QADigestPage />} />
          <Route path="/resources/tips" element={<WeeklyTipsPage />} />
          <Route path="/resources/calendar" element={<DeadlineCalendarPage />} />
          <Route path="/resources/activity-map" element={<ActivityMapPage />} />
          <Route path="/resources/admit-profiles" element={<AdmitProfilesPage />} />
          <Route path="/resources/admit-profiles/:id" element={<AdmitProfileDetailPage />} />

          {/* Catch-all: send unknown paths home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!hideFooter && <SiteFooter />}
    </div>
  );
}

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  if (!googleClientId) {
    console.error('Google Client ID not found in environment variables');
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId || ''}>
      <UserProvider>
        <Router>
          <AppShell />
        </Router>
      </UserProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
